import { useState, useEffect, useCallback } from "react";
import { Archive as ArchiveIcon, Download, FileText, Receipt, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeLabels = {
  quote: { label: "Preventivo", icon: FileText, cls: "bg-primary/20 text-primary" },
  invoice: { label: "Fattura", icon: Receipt, cls: "bg-green-500/20 text-green-400" },
  credit_note: { label: "Nota di Credito", icon: Receipt, cls: "bg-yellow-500/20 text-yellow-400" },
  ddt: { label: "DDT", icon: FileText, cls: "bg-blue-500/20 text-blue-400" },
};

const formatBytes = (b) => (b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`);

export const PdfArchiveSection = () => {
  const [docs, setDocs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/archive`);
      if (res.ok) setDocs(await res.json());
      setLoaded(true);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const download = async (doc) => {
    try {
      const res = await authFetch(`${API}/archive/${doc.archive_id}/download`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("Errore nel download");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    }
  };

  if (!loaded) return null;

  const shown = expanded ? docs : docs.slice(0, 5);

  return (
    <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-5" data-testid="pdf-archive-section">
      <button
        className="w-full flex items-center gap-2 text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid="pdf-archive-toggle"
      >
        <ArchiveIcon className="w-5 h-5 text-primary" />
        <span className="text-white font-medium flex-1">
          PDF Inviati — Copie Immutabili ({docs.length})
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      <p className="text-xs text-zinc-500 mt-1 ml-7">
        Copia storica non modificabile di ogni preventivo inviato e fattura scaricata
      </p>

      {docs.length > 0 && (
        <div className="mt-4 space-y-2">
          {shown.map((d) => {
            const t = typeLabels[d.doc_type] || typeLabels.quote;
            const Icon = t.icon;
            return (
              <div key={d.archive_id} className="flex items-center gap-3 p-2.5 bg-zinc-800/50 rounded-lg" data-testid={`archive-item-${d.archive_id}`}>
                <div className={`w-8 h-8 rounded flex items-center justify-center ${t.cls}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">
                    {t.label} {d.doc_number && `n. ${d.doc_number}`}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {new Date(d.created_at).toLocaleString("it-IT")} · {formatBytes(d.size_bytes)}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="border-zinc-700 h-7 text-xs" onClick={() => download(d)}
                  data-testid={`archive-download-${d.archive_id}`}>
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
              </div>
            );
          })}
          {!expanded && docs.length > 5 && (
            <button className="text-xs text-primary hover:underline ml-1" onClick={() => setExpanded(true)}>
              Mostra tutti ({docs.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};
